import React, { useState, useEffect } from "react";
import Hoc from "../../components/HOC";
import Loader from "../../components/Loader";
import { FaPlus, FaEdit, FaTrash, FaFileAlt, FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import RequirementForm from "./RequirementForm";
import ViewRequirementModal from "./ViewRequirementModal";
import moment from "moment";
import { getRecruitments, getRecruitmentById, createRecruitment, updateRecruitment, deleteRecruitment } from "../../Services/recruiterapis";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "A", label: "Active" },
  { value: "D", label: "Deactive" },
];

const Recruiting = () => {
  const [recruitments, setRecruitments] = useState([]);
  const [showTable, setShowTable] = useState(true); // Show table by default with static data
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editRequirementData, setEditRequirementData] = useState(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [filterValues, setFilterValues] = useState({
    status: statusOptions[1], // Default to Active (A)
    search: "",
  });

  // Add pagination state
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecruitments, setTotalRecruitments] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const navigate = useNavigate();

  const formatRequirementDate = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  };

  const paginatedRecruitments = recruitments;

  useEffect(() => {
    let isMounted = true;
    setIsPageLoading(true);

    const handler = setTimeout(async () => {
      try {
        const params = {
          page: Math.max(currentPage - 1, 0),
          size: rowsPerPage,
        };

        const statusValue = filterValues.status?.value;
        if (statusValue && statusValue !== "all") {
          params.stsCd = statusValue;
        }

        const trimmedSearch = searchInputValue.trim();
        if (trimmedSearch) {
          params.search = trimmedSearch;
        }

        const response = await getRecruitments(params);
        if (!isMounted) return;

        const normalized = (response?.content || []).map((item) => ({
          ...item,
          position: item.position || item.vendor || "",
          vendor: item.vendor || item.position || "",
          description: item.description || item.requirement || "",
          requirement: item.requirement || item.description || "",
          requirementDate: formatRequirementDate(item.requirementDate),
          expiryDate: formatRequirementDate(item.expiryDate),
        }));

        setRecruitments(normalized);
        setTotalRecruitments(response?.totalElements ?? normalized.length ?? 0);
        setTotalPages(response?.totalPages ?? 0);

        const apiTotalPages = response?.totalPages ?? 0;
        if (apiTotalPages > 0 && currentPage > apiTotalPages) {
          setCurrentPage(apiTotalPages);
        } else if (apiTotalPages === 0 && currentPage !== 1 && normalized.length === 0) {
          setCurrentPage(1);
        }
      } catch (error) {
        if (!isMounted) return;
        toast.error(error?.message || "Failed to fetch requirements");
        setRecruitments([]);
        setTotalRecruitments(0);
        setTotalPages(0);
      } finally {
        if (isMounted) {
          setIsPageLoading(false);
        }
      }
    }, 400);

    return () => {
      isMounted = false;
      clearTimeout(handler);
    };
  }, [currentPage, rowsPerPage, filterValues.status, searchInputValue, refreshToken]);

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const mapRecruitmentDetail = (data = {}) => ({
    id: data.id,
    position: data.position || "",
    location: data.location || "",
    clientName: data.clientName || "",
    vendor: data.vendor || "",
    rate: data.rate || "",
    description: data.description || data.requirement || "",
    requirement: data.requirement || data.description || "",
    stsCd: data.stsCd || "A",
    requirementDate: formatRequirementDate(data.requirementDate),
    expiryDate: formatRequirementDate(data.expiryDate),
    source: data.source || "",
  });

  const handleEdit = async (row) => {
    if (!row?.id) {
      toast.error("Invalid requirement selected");
      return;
    }

    setIsPageLoading(true);
    try {
      const detail = await getRecruitmentById(row.id);
      const normalized = mapRecruitmentDetail(detail || {});
      setEditRequirementData(normalized);
      setShowViewModal(false);
      setShowEditModal(true);
    } catch (error) {
      toast.error(error?.message || "Failed to fetch requirement details");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleView = (row) => {
    setEditRequirementData(row);
    setShowViewModal(true);
    setShowEditModal(false);
  };

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsPageLoading(true);
          await deleteRecruitment(id);
          Swal.fire({
            title: "Deleted!",
            text: "Requirement has been deleted.",
            icon: "success",
            confirmButtonColor: "#3085d6",
          });
          setRefreshToken((prev) => prev + 1);
          setCurrentPage(1);
        } catch (error) {
          Swal.fire({
            title: "Error!",
            text: error?.message || "Failed to delete requirement",
            icon: "error",
            confirmButtonColor: "#3085d6",
          });
        } finally {
          setIsPageLoading(false);
        }
      }
    });
  };

  const handleCreateSubmission = (row) => {
    // TODO: Implement submission creation logic
    navigate(`/requirements/${row.id}`);
    // You can navigate to a submission form or open a modal here
    // Example: navigate(`/submissions/create/${row.id}`);
  };

  const handleSaveRequirement = async (requirementData) => {
    // Ensure description is included in the payload
    const payload = {
      ...requirementData,
      description: requirementData.description || "",
      requirement: requirementData.description || "", // Map description to requirement for API
    };

    if (editRequirementData && editRequirementData.id) {
      // Edit mode - call API
      try {
        setIsPageLoading(true);
        const formattedDate = payload.requirementDate
          ? moment(payload.requirementDate, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true).format("YYYY-MM-DD")
          : "";

        const formattedExpiryDate = payload.expiryDate
          ? moment(payload.expiryDate, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true).format("YYYY-MM-DD")
          : "";

        const apiPayload = {
          clientName: payload.clientName || "",
          location: payload.location || "",
          rate: payload.rate || "",
          position: payload.position || "",
          requirement: payload.requirement || payload.description || "",
          requirementDate: formattedDate || moment().format("YYYY-MM-DD"),
          stsCd: payload.stsCd || "A",
          source: payload.source || "",
          expiryDate: formattedExpiryDate || moment().format("YYYY-MM-DD"),
        };

        await updateRecruitment(editRequirementData.id, apiPayload);
        toast.success("Requirement updated successfully!");
        setShowEditModal(false);
        setRefreshToken((prev) => prev + 1);
      } catch (error) {
        toast.error(error?.message || "Failed to update requirement");
        return;
      } finally {
        setIsPageLoading(false);
      }
    } else {
      // Add mode - call API
      try {
        setIsPageLoading(true);
        const formattedDate = payload.requirementDate
          ? moment(payload.requirementDate, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true).format("YYYY-MM-DD")
          : "";

        const formattedExpiryDate = payload.expiryDate
          ? moment(payload.expiryDate, ["MM/DD/YYYY", "YYYY-MM-DD", "MM-DD-YYYY"], true).format("YYYY-MM-DD")
          : "";

        const apiPayload = {
          clientName: payload.clientName || "",
          location: payload.location || "",
          rate: payload.rate || "",
          position: payload.position || "",
          requirement: payload.requirement || payload.description || "",
          requirementDate: formattedDate || moment().format("YYYY-MM-DD"),
          stsCd: payload.stsCd || "A",
          source: payload.source || "",
          expiryDate: formattedExpiryDate || moment().format("YYYY-MM-DD"),
        };

        await createRecruitment(apiPayload);
        toast.success("Requirement added successfully!");
        setShowAddModal(false);
        setRefreshToken((prev) => prev + 1);
      } catch (error) {
        toast.error(error?.message || "Failed to add requirement");
        return;
      } finally {
        setIsPageLoading(false);
      }
    }
    setCurrentPage(1);
    setEditRequirementData(null);
  };

  const requirementInitialValues = {
    location: "",
    clientName: "",
    vendor: "",
    rate: "",
    description: "",
    stsCd: "A",
    requirementDate: "",
    position: "",
    source: "",
    expiryDate: "",
  };

  // Handle status change
  const handleStatusChange = (value) => {
    const newStatus = statusOptions.find((opt) => opt.value === value) || statusOptions[1];
    setFilterValues((prev) => ({
      ...prev,
      status: newStatus,
    }));
  };

  // Handle page change
  const handleChangePage = (newPage) => {
    if (newPage === currentPage || newPage < 1) return;
    const effectiveTotalPages = totalPages || Math.ceil(totalRecruitments / rowsPerPage);
    if (effectiveTotalPages && newPage > effectiveTotalPages) return;
    setCurrentPage(newPage);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterValues.status, searchInputValue]);

  // Get pagination info
  const getPaginationInfo = () => {
    if (totalRecruitments === 0) return { start: 0, end: 0 };
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, totalRecruitments);
    return { start, end };
  };

  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const totalPageCount = totalPages || Math.ceil(totalRecruitments / rowsPerPage);
    if (!totalPageCount || totalPageCount <= 0) {
      return [];
    }
    let pages = [];
    
    // Simple case: show all pages if less than 5
    if (totalPageCount <= 5) {
      for (let i = 1; i <= totalPageCount; i++) {
        pages.push(i);
      }
      return pages;
    }
    
    // More complex case: show current page and some pages around it
    if (currentPage <= 3) {
      // Near start
      return [1, 2, 3, 4, 5];
    } else if (currentPage >= totalPageCount - 2) {
      // Near end
      return [
        totalPageCount - 4,
        totalPageCount - 3,
        totalPageCount - 2,
        totalPageCount - 1,
        totalPageCount
      ];
    } else {
      // Middle
      return [
        currentPage - 2,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        currentPage + 2
      ];
    }
  };

  // Get pagination info
  const { start, end } = getPaginationInfo();
  const pageNumbers = getPageNumbers();
  const totalPageCount = totalPages || Math.ceil(totalRecruitments / rowsPerPage);

  return (
    <div className="bg-gray-50">
      <Loader isLoading={isPageLoading} />

      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-end mb-6">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
          >
            <FaPlus className="mr-2" /> Add Requirement
          </button>
        </div>

        {showTable && (
          <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
            {/* Search and filters inside the table container */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                    placeholder="Search requirements by position, location, client name..."
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                  />
                </div>

                <div className="flex space-x-2">
                  <select
                    value={filterValues.status.value}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="px-5 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="A">Active</option>
                    <option value="D">Deactive</option>
                  </select>
                </div>
              </div>
            </div>
            {/* Table with sticky header */}
            <div className="overflow-y-auto" style={{ height: "52vh" }}>
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Actions
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Client Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Position
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Location
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Job Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Source
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Posted Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                      Expiry Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedRecruitments.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleView(row)}
                      className="cursor-pointer hover:bg-blue-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium w-1/8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateSubmission(row);
                            }}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-800 focus:outline-none focus:ring-2 focus:ring-green-300 transition"
                            title="Create Submission"
                            style={{ transition: "background 0.15s, color 0.15s" }}
                          >
                            <FaFileAlt size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(row);
                            }}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
                            title="Edit"
                            style={{ transition: "background 0.15s, color 0.15s" }}
                          >
                            <FaEdit size={16} />
                          </button>
                          {/* <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(row.id);
                            }}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <FaTrash size={16} />
                          </button> */}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-32">
                        <div
                          className="text-sm font-medium text-gray-900 max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={row.clientName || "N/A"}
                        >
                          {row.clientName || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-32">
                        <div
                          className="text-sm text-gray-900 max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={row.position || row.vendor || "N/A"}
                        >
                          {row.position || row.vendor || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-28">
                        <div
                          className="text-sm text-gray-900 max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={row.location || "N/A"}
                        >
                          {row.location || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <div
                          className="text-sm text-gray-900 max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap"
                          title={row.description || "N/A"}
                        >
                          {row.description || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <div className="text-sm text-gray-900">{row.rate || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <div className="text-sm text-gray-900">{row.source || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            row.stsCd === "A"
                              ? "bg-[#34D39920] text-[#34D399]"
                              : "bg-red-50 text-red-400"
                          }`}
                        >
                          {row.stsCd === "A" ? "Active" : "Closed"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <div className="text-sm text-gray-900">{row.requirementDate || "N/A"}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-1/8">
                        <div className="text-sm text-gray-900">{row.expiryDate || "N/A"}</div>
                      </td>


                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        
            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button 
                  onClick={() => currentPage > 1 && handleChangePage(1)}
                  disabled={currentPage === 1 || totalRecruitments === 0}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  First
                </button>
                <button 
                  onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                  disabled={currentPage === 1 || totalRecruitments === 0}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                  disabled={currentPage === totalPageCount || totalRecruitments === 0}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <button
                  onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                  disabled={currentPage === totalPageCount || totalRecruitments === 0}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  Last
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{start}</span> to <span className="font-medium">{end}</span> of <span className="font-medium">{totalRecruitments}</span> requirements
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <label htmlFor="rows-per-page" className="text-sm text-gray-700">
                      Rows per page:
                    </label>
                    <select
                      id="rows-per-page"
                      value={rowsPerPage}
                      onChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => currentPage > 1 && handleChangePage(1)}
                      disabled={currentPage === 1 || totalRecruitments === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      First
                    </button>
                    <button
                      onClick={() => currentPage > 1 && handleChangePage(currentPage - 1)}
                      disabled={currentPage === 1 || totalRecruitments === 0}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {pageNumbers.map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => handleChangePage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 ${
                          pageNum === currentPage
                            ? 'bg-blue-50 text-blue-600'
                            : 'bg-white text-gray-700'
                        } text-sm font-medium hover:bg-gray-50`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      onClick={() => currentPage < totalPageCount && handleChangePage(currentPage + 1)}
                      disabled={currentPage === totalPageCount || totalRecruitments === 0}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <button
                      onClick={() => currentPage < totalPageCount && handleChangePage(totalPageCount)}
                      disabled={currentPage === totalPageCount || totalRecruitments === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      Last
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Requirement Modal */}
        <RequirementForm
          show={showAddModal || showEditModal}
          onClose={() => {
            setShowAddModal(false);
            setShowEditModal(false);
            setEditRequirementData(null);
          }}
          onSave={handleSaveRequirement}
          initialValues={
            showEditModal && editRequirementData
              ? {
                  id: editRequirementData.id,
                  position: editRequirementData.position || "",
                  location: editRequirementData.location || "",
                  clientName: editRequirementData.clientName || "",
                  vendor: editRequirementData.vendor || "",
                  rate: editRequirementData.rate || "",
                  description: editRequirementData.description || "",
                  stsCd: editRequirementData.stsCd || "A",
                  requirementDate: editRequirementData.requirementDate || "",
                  requirement: editRequirementData.requirement || editRequirementData.description || "",
                  source: editRequirementData.source || "",
                  expiryDate: editRequirementData.expiryDate || "",
                }
              : requirementInitialValues
          }
          isEditMode={showEditModal}
        />

        {/* View Requirement Modal */}
        <ViewRequirementModal
          show={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setEditRequirementData(null);
          }}
          initialValues={{
            id: editRequirementData?.id || "",
            position: editRequirementData?.position || "",
            location: editRequirementData?.location || "",
            clientName: editRequirementData?.clientName || "",
            vendor: editRequirementData?.vendor || "",
            rate: editRequirementData?.rate || "",
            source: editRequirementData?.source || "",
            description: editRequirementData?.description || "",
            stsCd: editRequirementData?.stsCd || "A",
            requirementDate: editRequirementData?.requirementDate || "",
            expiryDate: editRequirementData?.expiryDate || "",
          }}
          onEditClick={() => {
            setShowViewModal(false);
            setShowEditModal(true);
          }}
        />
      </div>
    </div>
  );
};

export default Hoc(Recruiting);
